import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, FormGroup, Label, Spinner } from 'reactstrap';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import api from '../../api';

const MAP_CONTAINER_STYLE = { width: '100%', height: 220, borderRadius: 8 };
const MAP_OPTIONS = {
    disableDefaultUI: false,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    styles: [
        { featureType: 'poi', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', stylers: [{ visibility: 'simplified' }] },
    ],
};

const PREFECTURES = [
    '北海道','青森県','岩手県','宮城県','秋田県','山形県','福島県',
    '茨城県','栃木県','群馬県','埼玉県','千葉県','東京都','神奈川県',
    '新潟県','富山県','石川県','福井県','山梨県','長野県','岐阜県',
    '静岡県','愛知県','三重県','滋賀県','京都府','大阪府','兵庫県',
    '奈良県','和歌山県','鳥取県','島根県','岡山県','広島県','山口県',
    '徳島県','香川県','愛媛県','高知県','福岡県','佐賀県','長崎県',
    '熊本県','大分県','宮崎県','鹿児島県','沖縄県',
];

// ── Pill ボタン ──────────────────────────────────────────
const PillButton = ({ label, selected, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        style={{
            padding: '8px 16px',
            borderRadius: 20,
            fontSize: '0.85rem',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'all 0.15s',
            background: selected ? '#005c53' : '#fff',
            color: selected ? '#fff' : '#6b7280',
            border: selected ? '1px solid #005c53' : '1px solid #e0e4ea',
        }}
    >
        {label}
    </button>
);

// ── 質問ブロック ─────────────────────────────────────────
const QuestionBlock = ({ label, required, children }) => (
    <div>
        <div style={{ fontWeight: 700, color: '#1a1a2e', marginBottom: 12, fontSize: '0.95rem' }}>
            {label}
            {required && (
                <span style={{ marginLeft: 6, fontSize: '0.65rem', background: '#e53935', color: '#fff', borderRadius: 4, padding: '2px 6px', verticalAlign: 'middle' }}>
                    必須
                </span>
            )}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {children}
        </div>
    </div>
);

// ── ファイルアップロードボックス ──────────────────────────
const CameraIcon = () => (
    <svg width="36" height="36" fill="none" viewBox="0 0 24 24" stroke="#9ca3af" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
    </svg>
);

const FileIcon = () => (
    <svg width="36" height="36" fill="none" viewBox="0 0 24 24" stroke="#9ca3af" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
);

const FileUploadBox = ({ label, file, onChange, accept }) => {
    const inputId = `file-${label.replace(/\s/g, '-')}`;
    const isImage = accept?.includes('image');
    return (
        <div
            onClick={() => document.getElementById(inputId).click()}
            style={{
                border: '2px dashed #e0e4ea',
                borderRadius: 8,
                padding: '24px 16px',
                background: '#fafafa',
                textAlign: 'center',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
                minHeight: 110,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
            }}
        >
            {file ? (
                <>
                    {isImage ? (
                        <img src={URL.createObjectURL(file)} alt="preview" style={{ maxHeight: 80, maxWidth: '100%', objectFit: 'contain' }} />
                    ) : (
                        <>
                            <FileIcon />
                            <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>{file.name}</span>
                        </>
                    )}
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.45)', color: '#fff', fontSize: '0.72rem', textAlign: 'center', padding: '4px 0' }}>
                        タップして変更
                    </div>
                </>
            ) : (
                <>
                    {isImage ? <CameraIcon /> : <FileIcon />}
                    <span style={{ fontSize: '0.82rem', color: '#6b7280' }}>{label}</span>
                    <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>タップして追加</span>
                </>
            )}
            <input id={inputId} type="file" accept={accept} style={{ display: 'none' }} onChange={e => onChange(e.target.files[0] || null)} />
        </div>
    );
};

// ── テキスト入力スタイル ─────────────────────────────────
const textInputStyle = {
    width: '100%',
    padding: '11px 14px',
    border: '1px solid #e0e4ea',
    borderRadius: 6,
    fontSize: '0.9rem',
    color: '#1a1a2e',
    background: '#fff',
    outline: 'none',
    boxSizing: 'border-box',
};

const ConditionInputPage = () => {
    const customerId = parseInt(localStorage.getItem('customer_id') || '0', 10);
    const navigate = useNavigate();

    const { isLoaded: mapLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    });

    const mapRef = useRef(null);
    const onMapLoad = useCallback((map) => { mapRef.current = map; }, []);
    const onMapIdle = useCallback(() => {
        if (!mapRef.current) return;
        const center = mapRef.current.getCenter();
        if (!center) return;
        setSiteLat(center.lat());
        setSiteLng(center.lng());
    }, []);

    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Step 1
    const [workTypes, setWorkTypes] = useState([]);
    const [constructionWidth, setConstructionWidth] = useState('');
    const [constructionMotive, setConstructionMotive] = useState('');
    const [desiredPeriod, setDesiredPeriod] = useState('');
    const [locationPrefecture, setLocationPrefecture] = useState('');
    const [locationCity, setLocationCity] = useState('');
    const [customerType, setCustomerType] = useState('');
    const [howFound, setHowFound] = useState('');

    // Step 2
    const [panoramicPhoto, setPanoramicPhoto] = useState(null);
    const [sitePhoto, setSitePhoto] = useState(null);
    const [drawingFile, setDrawingFile] = useState(null);
    const [siteSameAsHome, setSiteSameAsHome] = useState(false);
    const [siteLat, setSiteLat] = useState(null);
    const [siteLng, setSiteLng] = useState(null);
    const [geoLoading, setGeoLoading] = useState(false);
    const [sitePostalCode, setSitePostalCode] = useState('');
    const [siteAddress, setSiteAddress] = useState('');
    const [siteBuilding, setSiteBuilding] = useState('');

    // Step 3
    const [lastName, setLastName] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastNameKana, setLastNameKana] = useState('');
    const [firstNameKana, setFirstNameKana] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const [prefecture, setPrefecture] = useState('');
    const [city, setCity] = useState('');
    const [address, setAddress] = useState('');
    const [building, setBuilding] = useState('');
    const [phone1, setPhone1] = useState('');
    const [phone2, setPhone2] = useState('');
    const [phone3, setPhone3] = useState('');

    const toggleWorkType = (type) => {
        setWorkTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
    };

    const handleStep1Next = () => {
        if (workTypes.length === 0) { setError('工事種別を1つ以上選択してください。'); return; }
        setError(null); setStep(2); window.scrollTo(0, 0);
    };
    const handleStep2Next = () => { setError(null); setStep(3); window.scrollTo(0, 0); };

    const getLocation = () => {
        if (!navigator.geolocation) { setError('位置情報が利用できません。'); return; }
        setGeoLoading(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => { setSiteLat(pos.coords.latitude); setSiteLng(pos.coords.longitude); setGeoLoading(false); setError(null); },
            () => { setError('位置情報の取得に失敗しました。'); setGeoLoading(false); }
        );
    };

    const fillAddressFromPostal = async () => {
        const clean = postalCode.replace(/-/g, '');
        if (clean.length !== 7) { setError('郵便番号は7桁で入力してください。'); return; }
        try {
            const res = await fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${clean}`);
            const data = await res.json();
            if (data.results) {
                const r = data.results[0];
                setPrefecture(r.address1); setCity(r.address2 + r.address3); setError(null);
            } else { setError('該当する住所が見つかりませんでした。'); }
        } catch { setError('住所の取得に失敗しました。'); }
    };

    const fillSiteAddressFromPostal = async () => {
        const clean = sitePostalCode.replace(/-/g, '');
        if (clean.length !== 7) { setError('郵便番号は7桁で入力してください。'); return; }
        try {
            const res = await fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${clean}`);
            const data = await res.json();
            if (data.results) {
                const r = data.results[0];
                setLocationPrefecture(r.address1); setLocationCity(r.address2 + r.address3); setError(null);
            } else { setError('該当する住所が見つかりませんでした。'); }
        } catch { setError('住所の取得に失敗しました。'); }
    };

    const buildFormData = (projectId) => {
        const fd = new FormData();
        fd.append('project_id', projectId);
        workTypes.forEach(t => fd.append('work_types[]', t));
        if (constructionWidth)  fd.append('construction_width', constructionWidth);
        if (constructionMotive) fd.append('construction_motive', constructionMotive);
        if (desiredPeriod)      fd.append('desired_period', desiredPeriod);
        if (customerType)       fd.append('customer_type', customerType);
        if (howFound)           fd.append('how_found', howFound);
        if (panoramicPhoto)     fd.append('panoramic_photo', panoramicPhoto);
        if (sitePhoto)          fd.append('site_photo', sitePhoto);
        if (drawingFile)        fd.append('drawing_file', drawingFile);
        fd.append('site_same_as_home', siteSameAsHome ? '1' : '0');
        if (siteLat !== null)   fd.append('site_lat', siteLat);
        if (siteLng !== null)   fd.append('site_lng', siteLng);

        // 工事先住所：自宅と同じ場合は自宅住所（Step3）をそのまま複製、異なる場合はStep2の入力を使う
        if (siteSameAsHome) {
            if (prefecture)   fd.append('location_prefecture', prefecture);
            if (city)         fd.append('location_city', city);
            if (postalCode)   fd.append('site_postal_code', postalCode.replace(/-/g, ''));
            if (address)      fd.append('site_address', address);
            if (building)     fd.append('site_building', building);
        } else {
            if (locationPrefecture) fd.append('location_prefecture', locationPrefecture);
            if (locationCity)       fd.append('location_city', locationCity);
            if (sitePostalCode)     fd.append('site_postal_code', sitePostalCode.replace(/-/g, ''));
            if (siteAddress)        fd.append('site_address', siteAddress);
            if (siteBuilding)       fd.append('site_building', siteBuilding);
        }
        return fd;
    };

    const handleSubmit = async () => {
        if (!lastName || !firstName || !phone1 || !phone2 || !phone3) {
            setError('名前・電話番号は必須です。'); return;
        }
        setLoading(true); setError(null);
        if (!customerId) { setError('顧客情報が取得できません。ログインし直してください。'); setLoading(false); return; }
        const selectedContractors = JSON.parse(sessionStorage.getItem('selected_contractors') || '[]');
        if (selectedContractors.length === 0) { setError('業者が選択されていません。'); setLoading(false); return; }
        try {
            const projectsRes = await api.post('projects', { customer_id: Number(customerId), contractor_ids: selectedContractors });
            for (const project of projectsRes.data) {
                await api.post('conditions', buildFormData(project.id));
            }
            await api.put(`customers/${customerId}`, {
                last_name: lastName, first_name: firstName,
                last_name_kana: lastNameKana, first_name_kana: firstNameKana,
                postal_code: postalCode.replace(/-/g, ''),
                prefecture, city, address,
                building: building || null,
                phone: `${phone1}-${phone2}-${phone3}`,
            });
            sessionStorage.removeItem('selected_contractors');
            navigate('/customer/dashboard');
        } catch (err) {
            const data = err?.response?.data;
            const status = err?.response?.status;
            const msg = data?.message
                || (data?.errors ? Object.values(data.errors).flat().join(' / ') : null)
                || (status ? `エラー ${status}` : `ネットワークエラー: ${err.message}`)
                || '送信に失敗しました。';
            setError(msg);
        } finally { setLoading(false); }
    };

    const progressPct = step === 1 ? 33 : step === 2 ? 66 : 100;
    const stepLabel = `条件入力 ${step}/3`;

    const cardStyle = {
        background: '#fff',
        borderRadius: 8,
        padding: '24px 20px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        display: 'flex',
        flexDirection: 'column',
        gap: 28,
    };

    return (
        <div style={{ minHeight: '100vh', background: '#f5f6f8', paddingBottom: 40 }}>

            {/* プログレスバー */}
            <div style={{ height: 5, background: '#e0e4ea', position: 'relative' }}>
                <div style={{
                    position: 'absolute', top: 0, left: 0, height: '100%',
                    width: `${progressPct}%`,
                    background: '#005c53',
                    transition: 'width 0.6s ease',
                }} />
            </div>

            {/* セクションヘッダー */}
            <div style={{ background: '#141c3d', padding: '16px 0' }}>
                <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 16px' }}>
                    <h1 style={{ color: '#fff', fontSize: '1rem', fontWeight: 500, margin: 0 }}>{stepLabel}</h1>
                </div>
            </div>

            <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px 0' }}>

                {/* ══ STEP 1 ══ */}
                {step === 1 && (
                    <div style={cardStyle}>
                        <QuestionBlock label="どのような工事を希望ですか" required>
                            {['住宅の乗り入れ', '事業用の乗り入れ', '駐車場の舗装', 'その他'].map(t => (
                                <PillButton key={t} label={t} selected={workTypes.includes(t)} onClick={() => toggleWorkType(t)} />
                            ))}
                        </QuestionBlock>

                        <QuestionBlock label="乗り入れの工事幅はわかりますか？">
                            {['わからない', '1〜2 m', '2〜4 m', '4 m以上'].map(v => (
                                <PillButton key={v} label={v} selected={constructionWidth === v} onClick={() => setConstructionWidth(constructionWidth === v ? '' : v)} />
                            ))}
                        </QuestionBlock>

                        <QuestionBlock label="工事動機はなんですか？">
                            {['現在住んでいる建物', '建設予定の建物', '購入予定の建物', 'その他'].map(v => (
                                <PillButton key={v} label={v} selected={constructionMotive === v} onClick={() => setConstructionMotive(constructionMotive === v ? '' : v)} />
                            ))}
                        </QuestionBlock>

                        <QuestionBlock label="希望工期はいつですか？">
                            {['1ヶ月以内に工事を着工したい', '3ヶ月以内に工事を着工したい', '6ヶ月以内に工事を着工したい', '12ヶ月以内に工事を着工したい', '1年以上先', '未定'].map(v => (
                                <PillButton key={v} label={v} selected={desiredPeriod === v} onClick={() => setDesiredPeriod(desiredPeriod === v ? '' : v)} />
                            ))}
                        </QuestionBlock>

                        <QuestionBlock label="お客様の種別">
                            {['個人', '法人', '建設会社', 'その他'].map(v => (
                                <PillButton key={v} label={v} selected={customerType === v} onClick={() => setCustomerType(customerType === v ? '' : v)} />
                            ))}
                        </QuestionBlock>

                        <QuestionBlock label="「サービス名」をどこで知りましたか">
                            {['ネットの検索', '知人の紹介', 'テレビCM', 'HPやチラシを見て', 'ネットのバナー広告', 'その他'].map(v => (
                                <PillButton key={v} label={v} selected={howFound === v} onClick={() => setHowFound(howFound === v ? '' : v)} />
                            ))}
                        </QuestionBlock>

                        {error && <p style={{ color: '#e53935', fontSize: '0.85rem', margin: 0 }}>{error}</p>}

                        <button onClick={handleStep1Next} style={{
                            width: '100%', padding: '14px',
                            background: '#141c3d', color: '#fff',
                            border: 'none', borderRadius: 6,
                            fontWeight: 600, fontSize: '1rem', cursor: 'pointer',
                        }}>
                            保存して次へ
                        </button>
                    </div>
                )}

                {/* ══ STEP 2 ══ */}
                {step === 2 && (
                    <div style={cardStyle}>

                        {/* 画像添付 */}
                        <div>
                            <div style={{ fontWeight: 700, color: '#1a1a2e', marginBottom: 4, fontSize: '0.95rem' }}>画像添付</div>
                            <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: 14 }}>
                                写真の撮り方はこちら → <span style={{ color: '#005c53', cursor: 'pointer' }}>写真の撮り方ページへ遷移</span>
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                <FileUploadBox label="全景写真" file={panoramicPhoto} onChange={setPanoramicPhoto} accept="image/*" />
                                <FileUploadBox label="工事箇所の写真" file={sitePhoto} onChange={setSitePhoto} accept="image/*" />
                            </div>
                        </div>

                        {/* 図面 */}
                        <div>
                            <div style={{ fontWeight: 700, color: '#1a1a2e', marginBottom: 2, fontSize: '0.95rem' }}>図面</div>
                            <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: 2 }}>建築、手書き、CAD</p>
                            <p style={{ fontSize: '0.82rem', color: '#e53935', marginBottom: 10 }}>あれば図面を添付してください。</p>
                            <FileUploadBox label="図面ファイル（画像・PDF）" file={drawingFile} onChange={setDrawingFile} accept="image/*,.pdf,.dwg,.dxf" />
                        </div>

                        {/* 工事場所 */}
                        <div>
                            <div style={{ fontWeight: 700, color: '#1a1a2e', marginBottom: 4, fontSize: '0.95rem' }}>工事場所</div>
                            <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: 12 }}>
                                工事を行う場所の住所を入力してください。
                            </p>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer', fontSize: '0.9rem', color: '#1a1a2e' }}>
                                <input
                                    type="checkbox"
                                    checked={siteSameAsHome}
                                    onChange={e => setSiteSameAsHome(e.target.checked)}
                                    style={{ width: 18, height: 18 }}
                                />
                                自宅と同じ場所
                            </label>
                            {siteSameAsHome && (
                                <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: 8 }}>
                                    ✓ 次のステップで自宅住所を入力していただきます
                                </p>
                            )}
                            {!siteSameAsHome && (
                                <>
                                    <div style={{ marginBottom: 16 }}>
                                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 8 }}>
                                            <div style={{ flex: 1 }}>
                                                <label style={{ fontSize: '0.78rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>郵便番号</label>
                                                <input value={sitePostalCode} onChange={e => setSitePostalCode(e.target.value)} placeholder="1234567（ハイフンなし）" maxLength={8} style={textInputStyle} />
                                            </div>
                                            <button onClick={fillSiteAddressFromPostal} style={{
                                                padding: '11px 14px', background: '#141c3d', color: '#fff',
                                                border: 'none', borderRadius: 6, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                                            }}>
                                                住所自動入力
                                            </button>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                                            <div>
                                                <label style={{ fontSize: '0.78rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>都道府県</label>
                                                <select value={locationPrefecture} onChange={e => setLocationPrefecture(e.target.value)} style={textInputStyle}>
                                                    <option value="">都道府県を選択</option>
                                                    {PREFECTURES.map(p => <option key={p}>{p}</option>)}
                                                </select>
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '0.78rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>市区町村</label>
                                                <input value={locationCity} onChange={e => setLocationCity(e.target.value)} placeholder="渋谷区" style={textInputStyle} />
                                            </div>
                                        </div>
                                        <div style={{ marginBottom: 8 }}>
                                            <label style={{ fontSize: '0.78rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>番地</label>
                                            <input value={siteAddress} onChange={e => setSiteAddress(e.target.value)} style={textInputStyle} />
                                        </div>
                                        <div>
                                            <label style={{ fontSize: '0.78rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>建物名</label>
                                            <input value={siteBuilding} onChange={e => setSiteBuilding(e.target.value)} style={textInputStyle} />
                                        </div>
                                    </div>
                                    <p style={{ fontSize: '0.78rem', color: '#e53935', marginBottom: 8 }}>
                                        「現在地を取得」ボタンで、地図上の正確な位置もあわせて指定できます（任意）。
                                    </p>
                                    <button
                                        onClick={getLocation}
                                        disabled={geoLoading}
                                        style={{
                                            width: '100%', padding: '12px',
                                            background: '#fff', color: '#141c3d',
                                            border: '2px solid #141c3d', borderRadius: 6,
                                            fontWeight: 600, fontSize: '0.9rem',
                                            cursor: geoLoading ? 'default' : 'pointer',
                                            marginBottom: 12,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                        }}
                                    >
                                        {geoLoading ? <Spinner size="sm" /> : '現在地を取得'}
                                    </button>
                                    {siteLat && siteLng && mapLoaded && (
                                        <>
                                            <p style={{ fontSize: '0.78rem', color: '#6b7280', textAlign: 'center', marginBottom: 6 }}>
                                                地図を動かして中央の＋を工事場所に合わせてください
                                            </p>
                                            <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden' }}>
                                                <GoogleMap
                                                    mapContainerStyle={MAP_CONTAINER_STYLE}
                                                    center={{ lat: siteLat, lng: siteLng }}
                                                    zoom={17}
                                                    options={{ ...MAP_OPTIONS, gestureHandling: 'greedy' }}
                                                    onLoad={onMapLoad}
                                                    onIdle={onMapIdle}
                                                />
                                                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none', zIndex: 1 }}>
                                                    <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                                                        <line x1="0" y1="22" x2="18" y2="22" stroke="#1a1a1a" strokeWidth="2" />
                                                        <line x1="26" y1="22" x2="44" y2="22" stroke="#1a1a1a" strokeWidth="2" />
                                                        <line x1="22" y1="0" x2="22" y2="18" stroke="#1a1a1a" strokeWidth="2" />
                                                        <line x1="22" y1="26" x2="22" y2="44" stroke="#1a1a1a" strokeWidth="2" />
                                                        <circle cx="22" cy="22" r="4" fill="#1a73e8" />
                                                        <circle cx="22" cy="22" r="7" fill="none" stroke="#1a73e8" strokeWidth="2" />
                                                    </svg>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                        </div>

                        {error && <p style={{ color: '#e53935', fontSize: '0.85rem', margin: 0 }}>{error}</p>}

                        <div style={{ display: 'flex', gap: 10 }}>
                            <button onClick={() => { setStep(1); window.scrollTo(0, 0); }} style={{
                                flex: 1, padding: '13px',
                                background: '#fff', color: '#1a1a2e',
                                border: '2px solid #e0e4ea', borderRadius: 6,
                                fontWeight: 500, fontSize: '0.9rem', cursor: 'pointer',
                            }}>
                                戻る
                            </button>
                            <button onClick={handleStep2Next} style={{
                                flex: 2, padding: '13px',
                                background: '#141c3d', color: '#fff',
                                border: 'none', borderRadius: 6,
                                fontWeight: 600, fontSize: '1rem', cursor: 'pointer',
                            }}>
                                保存して次へ
                            </button>
                        </div>
                    </div>
                )}

                {/* ══ STEP 3 ══ */}
                {step === 3 && (
                    <div style={cardStyle}>

                        {/* 名前 */}
                        <div>
                            <div style={{ fontWeight: 700, color: '#1a1a2e', marginBottom: 10, fontSize: '0.95rem' }}>
                                名前
                                <span style={{ marginLeft: 6, fontSize: '0.65rem', background: '#e53935', color: '#fff', borderRadius: 4, padding: '2px 6px', verticalAlign: 'middle' }}>必須</span>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                                <div>
                                    <label style={{ fontSize: '0.78rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>姓</label>
                                    <input value={lastName} onChange={e => setLastName(e.target.value)} placeholder="山田" style={textInputStyle} />
                                </div>
                                <div>
                                    <label style={{ fontSize: '0.78rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>名</label>
                                    <input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="太郎" style={textInputStyle} />
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 6 }}>
                                <div>
                                    <label style={{ fontSize: '0.78rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>セイ（カナ）</label>
                                    <input value={lastNameKana} onChange={e => setLastNameKana(e.target.value)} placeholder="ヤマダ" style={textInputStyle} />
                                </div>
                                <div>
                                    <label style={{ fontSize: '0.78rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>メイ（カナ）</label>
                                    <input value={firstNameKana} onChange={e => setFirstNameKana(e.target.value)} placeholder="タロウ" style={textInputStyle} />
                                </div>
                            </div>
                            <p style={{ fontSize: '0.78rem', color: '#9ca3af', margin: 0 }}>※カナは全角カタカナで入力してください</p>
                        </div>

                        {/* 自宅住所 */}
                        <div>
                            <div style={{ fontWeight: 700, color: '#1a1a2e', marginBottom: 10, fontSize: '0.95rem' }}>自宅住所</div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 8 }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ fontSize: '0.78rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>
                                        郵便番号
                                        <span style={{ marginLeft: 4, fontSize: '0.65rem', background: '#e53935', color: '#fff', borderRadius: 4, padding: '2px 5px' }}>必須</span>
                                    </label>
                                    <input value={postalCode} onChange={e => setPostalCode(e.target.value)} placeholder="1234567（ハイフンなし）" maxLength={8} style={textInputStyle} />
                                </div>
                                <button onClick={fillAddressFromPostal} style={{
                                    padding: '11px 14px', background: '#141c3d', color: '#fff',
                                    border: 'none', borderRadius: 6, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                                }}>
                                    住所自動入力
                                </button>
                            </div>
                            {[
                                { label: '都道府県', req: true, el: (
                                    <select value={prefecture} onChange={e => setPrefecture(e.target.value)} style={textInputStyle}>
                                        <option value=""></option>
                                        {PREFECTURES.map(p => <option key={p}>{p}</option>)}
                                    </select>
                                )},
                                { label: '市区町村', req: true, el: <input value={city} onChange={e => setCity(e.target.value)} style={textInputStyle} /> },
                                { label: '番地', req: true, el: <input value={address} onChange={e => setAddress(e.target.value)} style={textInputStyle} /> },
                                { label: '建物名', req: false, el: <input value={building} onChange={e => setBuilding(e.target.value)} style={textInputStyle} /> },
                            ].map(({ label, req, el }) => (
                                <div key={label} style={{ marginBottom: 8 }}>
                                    <label style={{ fontSize: '0.78rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>
                                        {label}
                                        {req
                                            ? <span style={{ marginLeft: 4, fontSize: '0.65rem', background: '#e53935', color: '#fff', borderRadius: 4, padding: '2px 5px' }}>必須</span>
                                            : <span style={{ marginLeft: 4, fontSize: '0.65rem', background: '#9ca3af', color: '#fff', borderRadius: 4, padding: '2px 5px' }}>任意</span>
                                        }
                                    </label>
                                    {el}
                                </div>
                            ))}
                        </div>

                        {/* 電話番号 */}
                        <div>
                            <div style={{ fontWeight: 700, color: '#1a1a2e', marginBottom: 4, fontSize: '0.95rem' }}>
                                電話番号
                                <span style={{ marginLeft: 6, fontSize: '0.65rem', background: '#e53935', color: '#fff', borderRadius: 4, padding: '2px 6px', verticalAlign: 'middle' }}>必須</span>
                            </div>
                            <p style={{ fontSize: '0.78rem', color: '#9ca3af', marginBottom: 8 }}>※数字のみ入力してください</p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <input value={phone1} onChange={e => setPhone1(e.target.value)} placeholder="090" inputMode="numeric" style={{ ...textInputStyle, textAlign: 'center' }} />
                                <span style={{ color: '#6b7280', fontSize: '1.1rem' }}>ー</span>
                                <input value={phone2} onChange={e => setPhone2(e.target.value)} placeholder="1234" inputMode="numeric" style={{ ...textInputStyle, textAlign: 'center' }} />
                                <span style={{ color: '#6b7280', fontSize: '1.1rem' }}>ー</span>
                                <input value={phone3} onChange={e => setPhone3(e.target.value)} placeholder="5678" inputMode="numeric" style={{ ...textInputStyle, textAlign: 'center' }} />
                            </div>
                        </div>

                        {error && <p style={{ color: '#e53935', fontSize: '0.85rem', margin: 0 }}>{error}</p>}

                        <div style={{ display: 'flex', gap: 10 }}>
                            <button onClick={() => { setStep(2); window.scrollTo(0, 0); }} style={{
                                flex: 1, padding: '13px',
                                background: '#fff', color: '#1a1a2e',
                                border: '2px solid #e0e4ea', borderRadius: 6,
                                fontWeight: 500, fontSize: '0.9rem', cursor: 'pointer',
                            }}>
                                戻る
                            </button>
                            <button onClick={handleSubmit} disabled={loading} style={{
                                flex: 2, padding: '13px',
                                background: '#141c3d', color: '#fff',
                                border: 'none', borderRadius: 6,
                                fontWeight: 600, fontSize: '1rem',
                                cursor: loading ? 'default' : 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                            }}>
                                {loading ? <Spinner size="sm" /> : '完了'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ConditionInputPage;
